import { useCallback, useEffect, useState } from 'react';

/**
 * Lecture serveur d'un ecran d'administration : une promesse, trois issues.
 *
 * Meme patron que les ecrans de comptes, mis en hook parce que l'ecran du stock
 * enchaine **deux** lectures (la recherche, puis la trace) :
 *
 * - **l'attente est deduite, jamais ecrite** : l'issue rangee porte la lecture et
 *   le numero de tentative qui l'ont produite, et tant qu'elle ne correspond pas
 *   a la lecture courante, c'est qu'on attend encore. Un `setState('loading')`
 *   pose dans l'effet couterait un rendu de plus a chaque passage ;
 * - **un echec de relecture n'efface pas ce qui etait lu** : `data` garde la
 *   derniere reponse obtenue de la meme lecture, `error` dit l'echec par-dessus ;
 * - **`read` absent = rien a lire** : l'etat est `idle` et aucune requete ne part.
 *   C'est ce qui garde l'invite initiale sans appel — chaque lecture d'une route
 *   d'administration ecrit une ligne d'audit (ADR 0035 §4).
 *
 * `read` doit etre **stable** (`useCallback` sur ses parametres) : sa reference
 * identifie la lecture, et en changer relance la requete.
 */
export type ServerRead<T> =
  | { readonly phase: 'idle' }
  | { readonly phase: 'loading'; readonly data: T | null }
  | { readonly phase: 'ready'; readonly data: T }
  | { readonly phase: 'failed'; readonly error: unknown; readonly data: T | null };

interface Outcome<T> {
  readonly read: () => Promise<T>;
  readonly attempt: number;
  readonly data: T | null;
  readonly error: unknown;
  readonly failed: boolean;
}

export const useServerRead = <T>(read: (() => Promise<T>) | null): { state: ServerRead<T>; reload: () => void } => {
  const [attempt, setAttempt] = useState(0);
  const [outcome, setOutcome] = useState<Outcome<T> | null>(null);

  useEffect(() => {
    if (read === null) {
      return;
    }

    // Le drapeau evite d'ecrire l'etat d'une reponse dont plus personne ne veut :
    // une autre recherche lancee entre-temps, un demontage pendant l'appel.
    let cancelled = false;

    const run = async (): Promise<void> => {
      try {
        const data = await read();

        if (!cancelled) {
          setOutcome({ read, attempt, data, error: null, failed: false });
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setOutcome((previous) => ({
            read,
            attempt,
            // Seule la derniere reponse de CETTE lecture est gardee : celle d'un
            // autre lot n'a rien a faire sous l'erreur de celui-ci.
            data: previous?.read === read ? previous.data : null,
            error,
            failed: true,
          }));
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [read, attempt]);

  const reload = useCallback((): void => {
    setAttempt((previous) => previous + 1);
  }, []);

  if (read === null) {
    return { state: { phase: 'idle' }, reload };
  }

  if (outcome?.read !== read) {
    return { state: { phase: 'loading', data: null }, reload };
  }

  if (outcome.attempt !== attempt) {
    return { state: { phase: 'loading', data: outcome.data }, reload };
  }

  return {
    state: outcome.failed
      ? { phase: 'failed', error: outcome.error, data: outcome.data }
      : { phase: 'ready', data: outcome.data as T },
    reload,
  };
};
